/**
 * @typedef {Object} ConversationState
 * @property {number} conversationId
 * @property {Object} subscription - ActionCable subscription
 * @property {Array} messages
 * @property {boolean} isLoading
 * @property {boolean} hasMore - older messages exist beyond the current window
 * @property {boolean} hasNewer - newer messages exist beyond the current window
 * @property {number} messagesOffset - DESC-order server offset of the newest edge of the window
 * @property {string|null} otherParticipantLastReadAt
 */

import { VIEWS, DIALOGS } from './chat-screen-state';
import { conversationActions } from './conversation-actions';
import { messageActions } from './message-actions';
import { groupActions } from './group-actions';
import { moderationActions } from './moderation-actions';
import { presenceActions } from './presence-actions';
import { statusActions } from './status-actions';
import { searchActions } from './search-actions';
import { deleteMessageActions } from './delete-message-actions';
import { editMessageActions } from './edit-message-actions';
import { chatFormatters } from './chat-formatters';
import { chatAnalytics } from './chat-analytics';
import { linkifyTrusted, escapeHtml } from '../../util/linkify';

export function chatOverlay({ currentUserId, canCreatePublicGroup = false, presencePreference = 'automatic', chatStatus = { preset: 'open_to_chat', label: window.i18n?.t('chat.chat_status.presets.open_to_chat') }, unreadChatCount = 0, currentLocale = 'en' }) {
  return {
    // ─────────────────────────────────────────
    // State
    // ─────────────────────────────────────────
    isOpen: false,
    currentUserId,
    currentLocale,
    isMinimized: false,
    conversations: [],
    hasMoreConversations: true,
    isLoadingMoreConversations: false,
    /** @type {ConversationState|null} */
    activeConversation: null,
    conversationFilter: 'all', // 'all' | 'unread' | 'groups'
    isLoading: false,
    error: null,
    sendError: null,
    voicePlaybackError: null,
    inboxSubscription: null,

    // Contact and group search state
    searchQuery: '',
    searchSuggestedUsers: [],
    searchUserResults: [],
    searchGroupResults: [],
    isLoadingSearchSuggestions: false,
    isSearchingChat: false,
    searchRequestId: 0,
    searchSubmitTracked: false,

    // Navigation state
    view: VIEWS.LIST,
    conversationReturnView: VIEWS.LIST,
    dialog: null,
    VIEWS,
    DIALOGS,

    // Groups browse state
    myGroupsExpanded: true,
    publicGroupsExpanded: true,
    publicGroups: [],
    isLoadingPublicGroups: false,
    publicGroupSearchQuery: '',
    publicGroupSearchResults: [],
    isSearchingPublicGroups: false,
    publicGroupsShowAll: false,
    pendingGroupIds: new Set(),
    requestingGroupId: null,
    requestNote: '',
    requestSubmitted: false,
    groupJoined: false,
    isSendingRequest: false,

    // New message dialog state
    canCreatePublicGroup,
    newGroupName: '',
    newGroupIsPublic: false,
    newGroupDescription: '',
    newGroupJoinPolicy: 'open',
    duplicateGroupName: null,
    selectedUsers: [],
    userSearchQuery: '',
    userSearchResults: [],
    isSearchingUsers: false,
    isCreatingGroup: false,
    suggestedUsers: [],
    isLoadingSuggested: false,
    duplicateGroup: null,
    duplicateMembersRequestId: 0,

    // Report dialog state
    reportingMessage: null,
    reportReasons: [],
    reportDescription: '',
    reportFollowStatus: false,
    reportBlock: false,
    showBlockConfirm: false,
    isSubmittingReport: false,
    reportError: null,

    // Block dialog state
    blockingConversationId: null,
    isSubmittingBlock: false,
    blockError: null,
    blockedUserIds: [],
    blockSuccess: false,
    blockSuccessMessage: '',
    showUnblockConfirm: false,

    // Manage members state
    manageMembersSearchQuery: '',
    selectedMember: null,
    removeMemberConfirm: false,
    deactivateGroupConfirm: false,
    reactivateGroupConfirm: false,

    // Add members dialog state
    addMembersDialogOpen: false,
    isAddingMembers: false,
    addMembersError: null,

    // Hidden messages state
    hiddenConversations: [],
    isLoadingHidden: false,
    blockedConversations: [],
    isLoadingBlocked: false,
    hideConversationSuccess: false,
    _hideConversationSuccessTimer: null,
    lastHiddenConversationId: null,
    lastHiddenConversationIds: [],
    unhideConversationSuccess: false,
    reportSuccess: false,
    reportBlocked: false,
    hiddenUnreadCount: 0,

    // Favorite messages state
    favoriteConversations: [],
    isLoadingFavorites: false,
    isLoadingMoreFavorites: false,
    hasMoreFavoriteConversations: false,
    expandedFavoriteConversationIds: [],
    loadingMoreFavoriteMessageIds: [],
    highlightedMessageId: null,

    // Invalid conversation state
    invalidConversationToast: false,

    // Presence / active status state
    presencePreference,
    presenceSubscription: null,

    // Current user's chat status ({ preset, label })
    chatStatus,

    // Conversation list inline menu
    listMenuConvId: null,

    // Inbox select mode (mark conversations as read)
    selectMode: false,
    selectedConversationIds: [],
    markReadSuccess: false,
    markReadAll: false,
    markReadCount: 0,
    markUnreadSuccess: false,
    markUnreadCount: 0,

    // ─────────────────────────────────────────
    // Lifecycle & UI
    // ─────────────────────────────────────────
    init() {
      this.loadBlockedUsers();
      this.loadConversations();
      this.loadHiddenUnreadCount();

      // Seeded from the render because the count spans conversations this
      // client hasn't loaded; the inbox channel keeps it current.
      this.$store.chat.totalUnreadCount = unreadChatCount;

      this.inboxSubscription = this.$cable.subscribeToInbox({
        onMessage: (conversationId, message) => {
          this.handleInboxMessage(conversationId, message);
        },
        onMessageDeleted: (conversationId, messageId) => {
          this.handleMessageDeleted(conversationId, messageId);
        },
        onMessageEdited: (conversationId, message) => {
          this.handleMessageEdited(conversationId, message);
        },
        onMemberAdded: (conversationId) => {
          this.handleMemberAdded(conversationId);
        },
        onMemberRemoved: (conversationId) => {
          // Immediately mark as removed so the banner shows when the conversation
          // is opened, even if it was closed when the removal happened.
          const conv = this.conversations.find((c) => c.id === conversationId);
          if (conv) {
            conv.is_removed = true;
            conv.current_user_status = 'removed';
          }
          // Also kill the active subscription if this conversation is currently open
          // so the user stops receiving live messages immediately.
          if (this.activeConversationId === conversationId && this.activeConversation?.subscription) {
            this.activeConversation.subscription.unsubscribe();
          }
          this.reloadConversation(conversationId);
        },
        onUserBlocked: (conversationId) => {
          // Optimistically show the blocked notice, then sync authoritative state from server.
          const conv = this.conversations.find((c) => c.id === conversationId);
          if (conv) {
            conv.is_blocked = true;
          }
          this.reloadConversation(conversationId);
        },
        onUserUnblocked: (conversationId) => {
          // Optimistically clear the blocked notice, then sync authoritative state from server.
          const conv = this.conversations.find((c) => c.id === conversationId);
          if (conv) {
            conv.is_blocked = false;
          }
          this.reloadConversation(conversationId);
        },
        onContactBlocked: (conversationId, blockedId) => {
          this.rememberBlockedUserId(blockedId);
          const conv = this.conversationForContactBlock(conversationId, blockedId);
          if (conv) {
            this.moveConversationToBlocked(conv);
          } else if (conversationId) {
            this.reloadConversation(conversationId);
          }
        },
        onContactUnblocked: (conversationId, blockedId) => {
          this.blockedUserIds = this.blockedUserIds.filter((id) => id !== blockedId);
          const conv = this.conversationForContactBlock(conversationId, blockedId);
          if (conv) {
            this.moveConversationFromBlocked(conv);
          } else if (conversationId) {
            this.reloadConversation(conversationId);
          }
        },
        onConversationDeactivated: (conversationId) => {
          const conv = this.getConversation(conversationId);
          if (conv) {
            conv.is_deactivated = true;
          }
        },
        onConversationReactivated: (conversationId) => {
          const conv = this.getConversation(conversationId);
          if (conv) {
            conv.is_deactivated = false;
          }
        },
        onUnreadNotificationCount: (count) => {
          this.handleUnreadNotificationCount(count);
        },
        onUnreadChatCount: (count) => {
          this.$store.chat.totalUnreadCount = count;
        },
        onConversationHidden: (conversationId) => {
          this.handleConversationHidden(conversationId);
        },
        onConversationUnhidden: (conversationId) => {
          this.handleConversationUnhidden(conversationId);
        }
      });

      this.presenceSubscription = this.subscribePresence();
    },

    destroy() {
      if (this.inboxSubscription) {
        this.inboxSubscription.unsubscribe();
      }
      if (this.presenceSubscription) {
        this.presenceSubscription.unsubscribe();
      }
    },

    toggle() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.isMinimized = false;
        this.trackChat('chat', 'open', 'floating-button');
      }
    },

    // Opens the conversation list. A chat with yourself has no other participant.
    openOverlay({ source } = {}) {
      this.isOpen = true;
      this.isMinimized = false;
      this.dialog = null;
      if (this.activeConversation) {
        this.closeConversation();
      } else {
        this.view = VIEWS.LIST;
      }
      this.trackChat('chat', 'open', source || 'overlay');
    },

    minimize() {
      this.isMinimized = true;
    },

    expand() {
      this.isMinimized = false;
    },

    close() {
      this.isOpen = false;
      this.dialog = null;
      this.exitSelectMode();
      this.closeConversation();

      // Refresh conversations to get accurate unread counts
      this.loadConversations();
    },

    // ─────────────────────────────────────────
    // View-switching hooks (overridden by chat-fullpage)
    // ─────────────────────────────────────────
    onConversationOpened() {
      this.view = VIEWS.CONVERSATION;
    },

    onConversationClosed() {
      this.view = this.conversationReturnView || VIEWS.LIST;
      this.conversationReturnView = VIEWS.LIST;
    },

    // ─────────────────────────────────────────
    // Shared Helpers
    // ─────────────────────────────────────────
    // Defaults to the active conversation; pass an id to look up a specific one.
    getConversation(conversationId = this.activeConversationId) {
      return (
        this.conversations.find((c) => c.id === conversationId) ||
        this.hiddenConversations.find((c) => c.id === conversationId) ||
        this.blockedConversations.find((c) => c.id === conversationId)
      );
    },

    rememberBlockedUserId(userId) {
      if (userId == null || this.blockedUserIds.includes(userId)) return;
      this.blockedUserIds = this.blockedUserIds.concat(userId);
    },

    conversationForContactBlock(conversationId, blockedId) {
      if (conversationId) {
        const byId = this.getConversation(conversationId);
        if (byId) return byId;
      }
      if (blockedId == null) return null;
      return (
        this.conversations.find((c) => c.other_participant?.id === blockedId) ||
        this.hiddenConversations.find((c) => c.other_participant?.id === blockedId) ||
        this.blockedConversations.find((c) => c.other_participant?.id === blockedId)
      );
    },

    get activeConversationId() {
      return this.activeConversation?.conversationId ?? null;
    },

    // On the Groups tab, only the user's joined groups are selectable.
    get showSelectBar() {
      if (this.selectMode) return true;
      if (this.isLoading || this.error) return false;

      return this.conversationFilter === 'groups'
        ? this.myGroups().length > 0
        : this.filteredConversations.length > 0;
    },

    get filteredConversations() {
      if (this.conversationFilter === 'unread') {
        return this.floatDrafts(this.conversations.filter((c) => this.isConversationUnread(c)));
      }
      if (this.conversationFilter === 'groups') {
        return this.floatDrafts(this.conversations.filter((c) => c.conversation_type === 'group'));
      }
      return this.floatDrafts(this.conversations);
    },

    // A conversation holding an unsent draft sits at the top until the draft
    // goes, then falls back to its last-activity position. Derived rather than
    // sorted in place, so the stored order stays the server's.
    floatDrafts(conversations) {
      const drafts = this.$store.chat.drafts;
      if (Object.keys(drafts).length === 0) return conversations;

      const drafted = [];
      const rest = [];
      conversations.forEach((c) => (drafts[c.id] ? drafted : rest).push(c));
      return [...drafted, ...rest];
    },

    isOwnMessage(message) {
      return message.user_id === currentUserId;
    },

    showTranslatedFrom(message) {
      if (message.hidden) return false;
      return this.currentLocale === 'fr'
        ? !!(message.content_fr && message.original_lang !== 'fr')
        : !!(message.content && message.original_lang === 'fr');
    },

    showTranslateButton(message) {
      if (message.attachment_type) return false;
      return this.currentLocale === 'fr'
        ? !message.content_fr && !!message.content
        : !message.content && !!message.content_fr;
    },

    linkifiedContent(message) {
      const color = this.isOwnMessage(message) ? '#58D75D' : '#0A0A0A';
      const content = this.displayableContent(message);
      const mentions = message.mentions || [];

      if (!mentions.length) return linkifyTrusted(content, color);

      // Split on @{{uuid}} tokens, linkify plain-text segments separately,
      // and interleave resolved mention chips.
      const TOKEN_RE = /@\{\{([\w-]+)\}\}/g;
      const parts = [];
      let lastIndex = 0;
      let match;

      while ((match = TOKEN_RE.exec(content)) !== null) {
        if (match.index > lastIndex) {
          parts.push(linkifyTrusted(content.slice(lastIndex, match.index), color));
        }

        const uuid = match[1];
        const mention = mentions.find((m) => m.attributes.uuid === uuid);
        if (mention) {
          const name = mention.attributes.name;
          const href = mention.links.profile_link;
          parts.push(`<a class="chat-mention" href="${escapeHtml(href)}">@${escapeHtml(name)}</a>`);
        } else {
          // UUID not resolved (e.g. deactivated user) — render as plain text.
          parts.push(`@${uuid}`);
        }

        lastIndex = TOKEN_RE.lastIndex;
      }

      if (lastIndex < content.length) {
        parts.push(linkifyTrusted(content.slice(lastIndex), color));
      }

      return parts.join('');
    },

    // Accessibility description for an image attachment, in the viewer's
    // locale, falling back to the other language then the filename.
    attachmentAltText(message) {
      const en = message.attachment_alt_text;
      const fr = message.attachment_alt_text_fr;
      const localized = this.currentLocale === 'fr' ? (fr || en) : (en || fr);
      return localized || message.attachment_name || '';
    },

    hasAttachmentAltText(message) {
      return !!(message.attachment_alt_text || message.attachment_alt_text_fr);
    },

    isMessageRead(message) {
      // Only show read status for own messages
      if (!this.isOwnMessage(message)) return false;

      const conv = this.getConversation(this.activeConversationId);
      // Only show read receipts for direct conversations
      if (!conv || conv.conversation_type !== 'direct') return false;

      if (!this.activeConversation?.otherParticipantLastReadAt) return false;

      return new Date(this.activeConversation.otherParticipantLastReadAt) >= new Date(message.created_at);
    },

    // True once any other member has read up to this message. Combines the
    // server flag (for messages read before page load) with live read-receipt
    // state (otherParticipantLastReadAt), which survives message re-serialization.
    // Used to hide "Delete for everyone" the moment a message has been seen.
    messageReadByOthers(message) {
      if (!message) return false;
      if (message.read_by_others) return true;
      const readAt = this.activeConversation?.otherParticipantLastReadAt;
      return !!readAt && new Date(readAt) >= new Date(message.created_at);
    },

    // Error toasts are bound to their own state keys in _chat_toasts.html.erb,
    // so each caller names the key it owns; only the arm/clear mechanics and the
    // dismissal delay are shared.
    _errorTimers: {},

    showTransientError(key, message, ms = 6000) {
      if (!message) return;

      this[key] = message;
      clearTimeout(this._errorTimers[key]);
      this._errorTimers[key] = setTimeout(() => {
        this[key] = null;
      }, ms);
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messageContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    // ─────────────────────────────────────────
    // Domain Modules
    // ─────────────────────────────────────────
    ...conversationActions(),
    ...messageActions(currentUserId),
    ...groupActions(currentUserId),
    ...moderationActions(),
    ...presenceActions(currentUserId),
    ...statusActions(),
    ...searchActions(),
    ...deleteMessageActions(),
    ...editMessageActions(),
    ...chatFormatters(),
    ...chatAnalytics(),
  };
}